import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface UserMetric {
  userId: string;
  email: string;
  name: string;
  totalPortfolio: number;
  cash: number;
  buyingPower: number;
  totalTrades: number;
  error?: boolean;
}

interface UserMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: 'portfolio' | 'cash' | 'buyingPower' | 'trades';
}

/**
 * Modal to display individual user metrics for admin dashboard
 * Shows portfolio, cash, buying power, or total trades for all active users
 */
const UserMetricsModal: React.FC<UserMetricsModalProps> = ({ isOpen, onClose, metricType }) => {
  const [users, setUsers] = useState<UserMetric[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'name' | 'value'>('value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (isOpen) {
      fetchUserMetrics();
    }
  }, [isOpen]);

  const fetchUserMetrics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/admin/user-metrics');
      
      if (response.data?.success && response.data?.data?.users) {
        setUsers(response.data.data.users);
      } else {
        setError('Failed to load user metrics');
      }
    } catch (err) {
      console.error('Failed to fetch user metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user metrics');
    } finally {
      setIsLoading(false);
    }
  };

  const getMetricTitle = () => {
    switch (metricType) {
      case 'portfolio':
        return 'Total Portfolio by User';
      case 'cash':
        return 'Cash by User';
      case 'buyingPower':
        return 'Buying Power by User';
      case 'trades':
        return 'Total Trades by User';
      default:
        return 'User Metrics';
    }
  };

  const getMetricValue = (user: UserMetric): number => {
    switch (metricType) {
      case 'portfolio':
        return user.totalPortfolio;
      case 'cash':
        return user.cash;
      case 'buyingPower':
        return user.buyingPower;
      case 'trades':
        return user.totalTrades;
      default:
        return 0;
    }
  };

  const formatValue = (value: number): string => {
    if (metricType === 'trades') {
      return value.toLocaleString();
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getSortedUsers = () => {
    const sorted = [...users].sort((a, b) => {
      if (sortField === 'name') {
        const comparison = a.name.localeCompare(b.name);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const aValue = getMetricValue(a);
        const bValue = getMetricValue(b);
        const comparison = aValue - bValue;
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
    return sorted;
  };

  const toggleSort = (field: 'name' | 'value') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getTotalValue = () => {
    return users.reduce((sum, user) => sum + getMetricValue(user), 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 border border-neutral-700 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-700">
          <h2 className="text-xl sm:text-2xl font-bold text-white">{getMetricTitle()}</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors p-2"
            aria-label="Close modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
              <span className="ml-3 text-neutral-400">Loading user metrics...</span>
            </div>
          ) : error ? (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="mt-4 text-neutral-400">No active users with Alpaca keys found</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="mb-6 bg-neutral-800 border border-neutral-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Total Active Users</p>
                    <p className="text-2xl font-bold text-white">{users.length}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-neutral-400">Total {metricType === 'trades' ? 'Trades' : 'Value'}</p>
                    <p className="text-2xl font-bold text-cyan-400">{formatValue(getTotalValue())}</p>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-3 px-4">
                        <button
                          onClick={() => toggleSort('name')}
                          className="flex items-center space-x-2 text-sm font-medium text-neutral-400 hover:text-white transition-colors"
                        >
                          <span>User</span>
                          {sortField === 'name' && (
                            <svg className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          )}
                        </button>
                      </th>
                      <th className="text-right py-3 px-4">
                        <button
                          onClick={() => toggleSort('value')}
                          className="flex items-center justify-end space-x-2 text-sm font-medium text-neutral-400 hover:text-white transition-colors ml-auto"
                        >
                          <span>
                            {metricType === 'portfolio' && 'Portfolio Value'}
                            {metricType === 'cash' && 'Cash'}
                            {metricType === 'buyingPower' && 'Buying Power'}
                            {metricType === 'trades' && 'Total Trades'}
                          </span>
                          {sortField === 'value' && (
                            <svg className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          )}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedUsers().map((user) => (
                      <tr
                        key={user.userId}
                        className={`border-b border-neutral-800 hover:bg-neutral-800/50 transition-colors ${
                          user.error ? 'opacity-50' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-sm text-neutral-400">{user.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <p className={`font-semibold ${user.error ? 'text-red-400' : 'text-white'}`}>
                            {user.error ? 'Error' : formatValue(getMetricValue(user))}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserMetricsModal;
